import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

// Utils
import {
  formatPhoneNumber,
  rawPhoneNumber,
  isPhoneNumberValid
} from 'utils/string';

// Components
import Feature from 'components/atoms/Feature/';
import InputField from 'components/atoms/InputField';
import InvisibleRecaptcha from 'components/molecules/InvisibleRecaptcha';

// Utils
import {
  EMAIL_REGEX,
  CHARACTER_EMAIL_REGEX,
  SPECIAL_CHARACTER_EMAIL_REGEX,
  LIMIT_TEN_CHARACTERS
} from 'utils/regex';

// Constants
import { VEHICLE_ROUTE } from 'constants/routes';
import { NAVIGATION } from 'constants/navigations';
import { APP_FLOW } from 'constants/appFlow';
import { SEARCH_CRITERIA_FIND_ME } from 'constants/findMe';
import { GTM_API, GTM_RECAPTCHA_EVENTS } from 'constants/recaptcha';

import './CustomerVehicleHeader.scss';
const ns = 'customer-vehicle-header';
const { FORWARD } = NAVIGATION;
const { IM_NEW_HERE } = APP_FLOW;
const { PHONE, EMAIL, LICENSE_NUMBER } = SEARCH_CRITERIA_FIND_ME;
const { customerVehicles: customerVehiclesApi } = GTM_API;

class CustomerVehicleHeader extends React.Component {
  constructor(props) {
    super(props);
    this.searchFindMeInput = React.createRef();
    this.recaptcha = React.createRef();
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.formatSearchInput = this.formatSearchInput.bind(this);
    this.beforeSubmit = this.beforeSubmit.bind(this);
    this.checkRecaptchaStatus = this.checkRecaptchaStatus.bind(this);
    this.state = {
      isInvalid: false,
      inputValue: '',
      handlingChange: false,
      rawInputValue: '',
      isTouched: false
    };
  }

  componentWillUpdate() {
    if (
      this.props.inputValue !== this.state.inputValue &&
      !this.state.isTouched
    ) {
      this.setState({
        inputValue: this.props.inputValue
      });
    }
  }

  checkRecaptchaStatus(recaptcha) {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.addedNodes.length > 0) {
          const iframeArrayList = document.getElementsByTagName('iframe');
          const reCaptchaArrayList = [...iframeArrayList].filter(el =>
            el.src.includes('recaptcha/api2/bframe')
          );
          if (reCaptchaArrayList.length === 2) {
            observer.disconnect();
            recaptcha.initRecaptchaListener();
            setTimeout(() => recaptcha.execute(), 0);
          }
        }
      });
    });

    const config = { attributes: false, childList: true };
    observer.observe(document.body, config);
  }

  resetTouched = () => {
    this.setState({
      isTouched: false
    });
  };

  emailValid(email) {
    const reg = EMAIL_REGEX;
    this.setState({
      isInvalid: !reg.test(email),
      rawInputValue: email
    });
    return reg.test(email);
  }

  phoneNumberValid(phoneNumber) {
    const { dealerCountryCode } = this.props;

    if (isPhoneNumberValid(phoneNumber, dealerCountryCode)) {
      const formattedNumber = formatPhoneNumber(phoneNumber, dealerCountryCode);
      this.setState({
        isInvalid: false,
        inputValue: formattedNumber,
        rawInputValue: phoneNumber
      });
      return formattedNumber;
    } else {
      return false;
    }
  }

  licenseNumberValid(licenseNumber) {
    if (LIMIT_TEN_CHARACTERS.test(licenseNumber)) {
      this.setState({
        pageErrorType: null,
        rawInputValue: licenseNumber,
        search: true
      });
    }
    return LIMIT_TEN_CHARACTERS.test(licenseNumber);
  }

  formatSearchInput(inputValue, searchCriteria) {
    const { dealerCountryCode } = this.props;
    const formattedNumber = formatPhoneNumber(inputValue, dealerCountryCode);
    this.setState({
      inputValue
    });
    if (
      searchCriteria.indexOf(PHONE) !== -1 &&
      isPhoneNumberValid(formattedNumber, dealerCountryCode)
    ) {
      this.setState({
        rawInputValue: rawPhoneNumber(formattedNumber),
        inputValue: formattedNumber
      });
    } else if (
      searchCriteria.indexOf(EMAIL) !== -1 &&
      (SPECIAL_CHARACTER_EMAIL_REGEX.test(inputValue) ||
        CHARACTER_EMAIL_REGEX.test(inputValue))
    ) {
      this.setState({ inputValue });
    } else if (
      searchCriteria.indexOf(LICENSE_NUMBER) !== -1 &&
      LIMIT_TEN_CHARACTERS.test(inputValue)
    ) {
      this.setState({ inputValue: rawPhoneNumber(formattedNumber) });
    } else {
      this.setState({ inputValue: rawPhoneNumber(formattedNumber) });
    }
  }

  handleChange = searchCriteria => ({ target: { value } }) => {
    let isInvalid = true;
    let i;
    const validationFn = {
      [PHONE]: 'phoneNumberValid',
      [EMAIL]: 'emailValid',
      [LICENSE_NUMBER]: 'licenseNumberValid'
    };
    for (i = 0; i < searchCriteria.length && isInvalid; i += 1) {
      isInvalid = !this[validationFn[searchCriteria[i]]](value);
    }
    this.setState({
      handlingChange: true,
      inputValue: value,
      isTouched: true,
      isInvalid
    });
    this.formatSearchInput(value, searchCriteria);
    return !isInvalid;
  };

  gtmUserEventCustomerVehicle(fieldName, fieldValue) {
    const { gtmUserEvent, isRegistrationNumEnabled } = this.props;
    const eventElement = isRegistrationNumEnabled ? 'Registration, ' : '';
    let fieldNameFormatted;
    switch (fieldName) {
      case LICENSE_NUMBER:
        fieldNameFormatted = 'license number';
        break;
      case PHONE:
        fieldNameFormatted = 'phone';
        break;
      default:
        fieldNameFormatted = 'email';
        break;
    }
    gtmUserEvent(
      eventElement.concat('Phone Or Email Search Input'),
      'Submitted',
      `New ${fieldNameFormatted} Search`,
      'GTM_CURRENT_PAGE > Schedule Your Service Section > Choose Vehicle',
      fieldValue
    );
  }

  async handleSubmit(ev) {
    ev && ev.preventDefault();
    const { rawInputValue, isInvalid } = this.state;
    const {
      customerVehicles,
      isRecaptchaEnabledOnFindMe,
      gtmRecaptchaEvent,
      recaptchaToken
    } = this.props;
    const {
      tokenId,
      getCustomerVehicles,
      goTo,
      showAlert,
      customerVehiclesByField,
      setAppFlow
    } = this.props;
    if (!isInvalid && rawInputValue !== '') {
      this.searchFindMeInput.current.input.blur();
      const fieldName = customerVehiclesByField(rawInputValue);
      this.gtmUserEventCustomerVehicle(fieldName, rawInputValue);
      const success = await getCustomerVehicles(
        tokenId,
        fieldName,
        rawInputValue
      );
      isRecaptchaEnabledOnFindMe &&
        gtmRecaptchaEvent(
          GTM_RECAPTCHA_EVENTS.API_CALL,
          GTM_API.customerVehicles,
          recaptchaToken,
          success
        );
      if (customerVehicles.length <= 0) {
        showAlert();
        setAppFlow(IM_NEW_HERE);
        goTo(VEHICLE_ROUTE, FORWARD);
      } else {
        if (fieldName === LICENSE_NUMBER) {
          this.setState({
            inputValue: rawInputValue.toUpperCase()
          });
        }
      }
    }
  }

  buildSearchInputField(placeholder, searchCriteria) {
    const {
      onAddonClick,
      intl,
      isRecaptchaEnabledOnFindMe,
      setRecaptchaToken,
      gtmRecaptchaEvent,
      languageCode
    } = this.props;
    const { inputValue, isInvalid } = this.state;
    const searchAddonConf = {
      isGlyhicon: true,
      addonValue: 'back-close',
      iconClassName: `clickable ${ns}__icon`,
      onAddonClick
    };
    const inputClassNames = classNames({
      'search-input': true,
      [`${ns}__input-field`]: isInvalid
    });

    return (
      <FormattedMessage id={placeholder}>
        {placeholder => (
          <div>
            <InputField
              type={'search'}
              placeholder={placeholder}
              leftAddonConf={searchAddonConf}
              className={inputClassNames}
              iconTabIndex={0}
              iconAriaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.start.page',
                defaultMessage: 'Back to Start Page'
              })}
              inputAriaLabel={intl.formatMessage({
                id: 'c8.aria.introduce.email.or.phone',
                defaultMessage:
                  'Introduce your email or phone to list your vehicles'
              })}
              inputConf={{
                onChange: this.handleChange(searchCriteria),
                value: inputValue
              }}
              ref={this.searchFindMeInput}
            />
            {isRecaptchaEnabledOnFindMe && (
              <InvisibleRecaptcha
                setRecaptchaToken={setRecaptchaToken}
                languageCode={languageCode}
                gtmAction={gtmRecaptchaEvent}
                gtmApi={customerVehiclesApi}
                order={1}
                onResolveAction={async ev => {
                  await this.handleSubmit(ev);
                }}
                ref={this.recaptcha}
              />
            )}
          </div>
        )}
      </FormattedMessage>
    );
  }

  async beforeSubmit(ev) {
    ev.preventDefault();
    const { isRecaptchaEnabledOnFindMe } = this.props;

    if (isRecaptchaEnabledOnFindMe) {
      this.recaptcha.current.reset();
      this.checkRecaptchaStatus(this.recaptcha.current);
    } else {
      await this.handleSubmit(ev);
    }
  }

  render() {
    const { searchCriteriaList, isRegistrationNumEnabled } = this.props;
    return (
      <div>
        <form action="#" onSubmit={this.beforeSubmit}>
          <Feature
            name="$phoneEmailSearch"
            render={() =>
              this.buildSearchInputField(
                isRegistrationNumEnabled
                  ? 'c8.start.page.modal.registration.phone.or.email'
                  : 'c8.start.page.modal.phone.or.email',
                searchCriteriaList
              )
            }
          />
        </form>
      </div>
    );
  }
}

CustomerVehicleHeader.propTypes = {
  customerVehicles: PropTypes.array,
  customerVehiclesByField: PropTypes.func.isRequired,
  dealerCountryCode: PropTypes.string,
  getCustomerEmail: PropTypes.string.isRequired,
  getCustomerPhone: PropTypes.string.isRequired,
  getCustomerVehicles: PropTypes.func.isRequired,
  goTo: PropTypes.func.isRequired,
  gtmUserEvent: PropTypes.func.isRequired,
  inputValue: PropTypes.string,
  intl: PropTypes.object,
  isRegistrationNumEnabled: PropTypes.bool.isRequired,
  onAddonClick: PropTypes.func.isRequired,
  searchCriteriaList: PropTypes.array.isRequired,
  setAppFlow: PropTypes.func.isRequired,
  showAlert: PropTypes.func.isRequired,
  tokenId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isRecaptchaEnabledOnFindMe: PropTypes.bool.isRequired,
  setRecaptchaToken: PropTypes.func.isRequired,
  gtmRecaptchaEvent: PropTypes.func.isRequired,
  languageCode: PropTypes.string
};

export default CustomerVehicleHeader;



// WEBPACK FOOTER //
// ./src/components/molecules/CustomerVehicleHeader/CustomerVehicleHeader.js